import { useState, useEffect } from 'preact/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  CheckCircle, 
  XCircle, 
  Settings, 
  Bot, 
  Zap, 
  Shield, 
  Code,
  RefreshCw,
  AlertTriangle
} from 'lucide-preact';
import { ClaudeCodeSDKService } from '../services/claudeCodeSDK';
import { SDKConfiguration } from '../services/sdkConfig';
import { testSDKIntegration } from '../services/sdkTest';

interface SDKStatusProps {
  projectId: string;
  projectPath: string;
}

export function SDKStatus({ projectId, projectPath }: SDKStatusProps) {
  const [sdkService] = useState(() => {
    const config = {
      projectPath,
      model: 'claude-3-5-sonnet-20241022',
      maxTurns: 10,
      allowedTools: ['Read', 'Edit', 'Write', 'Glob', 'Grep', 'Bash', 'Task']
    };
    return new ClaudeCodeSDKService(config);
  });
  
  const [config, setConfig] = useState<SDKConfiguration | null>(null);
  const [availableTools, setAvailableTools] = useState<string[]>([]);
  const [slashCommands, setSlashCommands] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<any>(null);
  const [isTesting, setIsTesting] = useState(false);

  useEffect(() => {
    loadSDKStatus();
  }, []);

  const loadSDKStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Load configuration
      const sdkConfig = sdkService.getConfiguration();
      setConfig(sdkConfig);

      // Load available tools and slash commands
      const [tools, commands] = await Promise.all([
        sdkService.getAvailableTools(),
        sdkService.getSlashCommands()
      ]);

      setAvailableTools(tools);
      setSlashCommands(commands);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load SDK status');
    } finally {
      setIsLoading(false);
    }
  };

  const runSDKTest = async () => {
    try {
      setIsTesting(true);
      setTestResult(null);
      
      const result = await testSDKIntegration(projectPath);
      setTestResult(result);
    } catch (error) {
      setTestResult({
        success: false,
        message: `Test failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsTesting(false);
    }
  };

  const getPermissionModeColor = (mode: string) => {
    switch (mode) {
      case 'default': return 'bg-blue-100 text-blue-800';
      case 'acceptEdits': return 'bg-green-100 text-green-800';
      case 'bypassPermissions': return 'bg-red-100 text-red-800';
      case 'plan': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPermissionModeIcon = (mode: string) => {
    switch (mode) {
      case 'default': return <Shield className="h-3 w-3" />;
      case 'acceptEdits': return <CheckCircle className="h-3 w-3" />;
      case 'bypassPermissions': return <XCircle className="h-3 w-3" />;
      case 'plan': return <AlertTriangle className="h-3 w-3" />;
      default: return <Shield className="h-3 w-3" />;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 animate-spin" />
            Byte DevOps SDK Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Loading SDK status...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-500" />
            Byte DevOps SDK Error
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-500 mb-4">{error}</p>
          <Button onClick={loadSDKStatus} size="sm" variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-500" />
          Byte DevOps SDK Status
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Configuration Overview */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium">Model:</span>
            <div className="text-muted-foreground">{config?.model}</div>
          </div>
          <div>
            <span className="font-medium">Max Turns:</span>
            <div className="text-muted-foreground">{config?.maxTurns}</div>
          </div>
          <div>
            <span className="font-medium">Permission Mode:</span>
            <div className="flex items-center gap-1 mt-1">
              <Badge className={`text-xs ${getPermissionModeColor(config?.permissionMode || 'default')}`}>
                {getPermissionModeIcon(config?.permissionMode || 'default')}
                {config?.permissionMode || 'default'}
              </Badge>
            </div>
          </div>
          <div>
            <span className="font-medium">Partial Messages:</span>
            <div className="text-muted-foreground">
              {config?.includePartialMessages ? 'Enabled' : 'Disabled'}
            </div>
          </div>
        </div>

        {/* Tools */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Code className="h-4 w-4" />
            <span className="font-medium">Available Tools ({availableTools.length})</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {availableTools.slice(0, 8).map((tool) => (
              <Badge key={tool} variant="secondary" className="text-xs">
                {tool}
              </Badge>
            ))}
            {availableTools.length > 8 && (
              <Badge variant="outline" className="text-xs">
                +{availableTools.length - 8} more
              </Badge>
            )}
          </div>
        </div>

        {/* Slash Commands */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Zap className="h-4 w-4" />
            <span className="font-medium">Slash Commands ({slashCommands.length})</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {slashCommands.map((command) => (
              <Badge key={command} variant="outline" className="text-xs">
                {command}
              </Badge>
            ))}
          </div>
        </div>

        {/* System Prompts */}
        {(config?.customSystemPrompt || config?.appendSystemPrompt) && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Settings className="h-4 w-4" />
              <span className="font-medium">Custom Prompts</span>
            </div>
            <div className="space-y-2 text-sm">
              {config.customSystemPrompt && (
                <div>
                  <span className="font-medium">Custom:</span>
                  <div className="text-muted-foreground text-xs mt-1 p-2 bg-muted rounded">
                    {config.customSystemPrompt.substring(0, 100)}
                    {config.customSystemPrompt.length > 100 && '...'}
                  </div>
                </div>
              )}
              {config.appendSystemPrompt && (
                <div>
                  <span className="font-medium">Append:</span>
                  <div className="text-muted-foreground text-xs mt-1 p-2 bg-muted rounded">
                    {config.appendSystemPrompt.substring(0, 100)}
                    {config.appendSystemPrompt.length > 100 && '...'}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Test Result */}
        {testResult && (
          <div className={`p-3 rounded border ${
            testResult.success 
              ? 'bg-green-50 border-green-200 text-green-800' 
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              {testResult.success ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              <span className="font-medium">
                {testResult.success ? 'SDK Test Passed' : 'SDK Test Failed'}
              </span>
            </div>
            <p className="text-sm mb-2">{testResult.message}</p>
            {testResult.details && (
              <details className="text-xs">
                <summary className="cursor-pointer font-medium">Test Details</summary>
                <pre className="mt-2 p-2 bg-white/50 rounded text-xs overflow-auto">
                  {JSON.stringify(testResult.details, null, 2)}
                </pre>
              </details>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2 border-t">
          <Button onClick={loadSDKStatus} size="sm" variant="outline" className="flex-1">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Status
          </Button>
          <Button 
            onClick={runSDKTest} 
            size="sm" 
            variant="outline" 
            disabled={isTesting}
            className="flex-1"
          >
            {isTesting ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Bot className="h-4 w-4 mr-2" />
            )}
            {isTesting ? 'Testing...' : 'Test SDK'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
